import { useState, useEffect, useCallback } from 'react'
import {
  MdArrowBack,
  MdEdit,
  MdPerson,
  MdWork,
  MdPhone,
  MdEmail,
  MdLocationOn,
  MdFlight,
  MdHealthAndSafety,
  MdSchool,
  MdEmergency,
  MdRefresh,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import PilotCharts from '../../components/charts/PilotCharts'
import { Button, LoadingSpinner } from '../../components/ui'
import { usePilot } from '../../hooks/usePilot'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { capitalizeStatus } from '../../utils/string'
import { getWebsocketChannel } from '../../utils/websocket'

function PilotDetailPage() {
  const dispatch = useDispatch()
  const { pilotId } = useParams()
  const navigate = useNavigate()
  const [pilot, setPilot] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()

  const { getPilotAsync } = usePilot()

  const loadPilot = useCallback(async () => {
    try {
      setIsLoading(true)
      const pilotData = await getPilotAsync(pilotId)
      setPilot(pilotData)
    } catch (error) {
      console.error('Error loading pilot:', error)
    } finally {
      setIsLoading(false)
    }
  }, [pilotId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pilotId) {
      loadPilot()
    }
  }, [pilotId, loadPilot])

  useEffect(() => {
    const channelName = getWebsocketChannel(pilotId).pilotDetails

    if (channelName && pilotId) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (channelName && pilotId) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getDaysUntilExpiry = (dateString) => {
    if (!dateString) return null
    const expiryDate = new Date(dateString)
    const today = new Date()
    const diffTime = expiryDate - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const getExpiryStatus = (dateString) => {
    const daysUntil = getDaysUntilExpiry(dateString)
    if (daysUntil === null) return 'unknown'
    if (daysUntil < 0) return 'expired'
    if (daysUntil <= 30) return 'warning'
    return 'good'
  }

  // const getStatusIcon = (status) => {
  //   switch (status) {
  //     case 'active':
  //       return <MdWork className="text-success-600 dark:text-success-400" />
  //     case 'available':
  //       return <MdSchedule className="text-blue-600 dark:text-blue-400" />
  //     case 'on-leave':
  //       return <MdWarning className="text-warning-600 dark:text-warning-400" />
  //     default:
  //       return <MdPerson className="text-neutral-500 dark:text-neutral-400" />
  //   }
  // }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
      case 'available':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
      case 'on-leave':
        return 'bg-warning-100 dark:bg-warning-900 text-warning-600 dark:text-warning-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    }
  }

  const getExpiryColor = (status) => {
    switch (status) {
      case 'expired':
        return 'text-red-600 dark:text-red-400'
      case 'warning':
        return 'text-warning-600 dark:text-warning-400'
      case 'good':
        return 'text-success-600 dark:text-success-400'
      default:
        return 'text-neutral-500 dark:text-neutral-400'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!pilot) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
            Pilot not found
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The pilot you're looking for doesn't exist or has been removed.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2"
          >
            <MdArrowBack className="text-lg" />
            <span>Back to Pilots</span>
          </Button>
        </div>
      </div>
    )
  }

  const licenseExpiryStatus = getExpiryStatus(pilot.licenseExpiry)
  const medicalExpiryStatus = getExpiryStatus(pilot.dateOfNextMedical)

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="text-left">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 mb-4"
          >
            <MdArrowBack />
            <span>Back to Pilots</span>
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-neutral-900 dark:text-white mb-2 text-left">
              {pilot.firstName} {pilot.lastName}
            </h1>
            <p className="text-base lg:text-lg text-neutral-600 dark:text-neutral-400 text-left">
              {pilot.number} • {pilot.email}
            </p>
          </div>

          <div className="flex items-center space-x-3 flex-shrink-0">
            <Button variant="ghost" onClick={loadPilot} disabled={isLoading}>
              <MdRefresh className={isLoading ? 'animate-spin' : ''} />
            </Button>

            <div className="flex items-center space-x-2">
              {/* {getStatusIcon(pilot.status)} */}
              <span
                className={`px-3 lg:px-4 py-2 text-sm font-medium rounded-full ${getStatusColor(pilot.status)}`}
              >
                {capitalizeStatus(pilot.status)}
              </span>
            </div>

            <Button
              variant="primary"
              onClick={() => {
                /* TODO: Implement edit pilot */
              }}
              className="flex items-center space-x-2"
            >
              <MdEdit className="text-lg" />
              <span>Edit Pilot</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center space-x-2">
              <MdPerson className="text-primary-600 dark:text-primary-400" />
              <span>Personal Information</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Full Name
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {pilot.firstName} {pilot.lastName}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Pilot Number
                </label>
                <p className="text-neutral-900 dark:text-white">{pilot.number}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Date of Birth
                </label>
                <p className="text-neutral-900 dark:text-white">{formatDate(pilot.dateOfBirth)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Date of Hire
                </label>
                <p className="text-neutral-900 dark:text-white">{formatDate(pilot.dateOfHire)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Email
                </label>
                <div className="text-center">
                  <p className="text-neutral-900 dark:text-white flex items-center justify-center space-x-1">
                    <MdEmail className="text-sm" />
                    <span>{pilot.email}</span>
                  </p>
                </div>
              </div>
              <div className="text-center">
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Phone
                </label>
                <p className="text-neutral-900 dark:text-white flex items-center justify-center space-x-1">
                  <MdPhone className="text-sm" />
                  <span>{pilot.phone}</span>
                </p>
              </div>
            </div>

            {pilot.address && (
              <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400 flex items-center space-x-1">
                  <MdLocationOn className="text-sm" />
                  <span>Address</span>
                </label>
                <p className="text-neutral-900 dark:text-white">{pilot.address}</p>
              </div>
            )}
          </div>

          {/* Flight Experience */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center space-x-2">
              <MdFlight className="text-primary-600 dark:text-primary-400" />
              <span>Flight Experience</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Total Flight Hours
                </label>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                  {pilot.totalFlightHours?.toLocaleString() || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Instrument Rating
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {pilot.instrumentRating ? 'Yes' : 'No'}
                </p>
              </div>
            </div>

            {pilot.typeRatings && pilot.typeRatings.length > 0 && (
              <div className="mt-4 text-center justify-center items-center flex flex-col">
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Type Ratings
                </label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {pilot.typeRatings.map((rating, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium"
                    >
                      {rating}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Charts Section */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Training & Certification Analytics
            </h2>
            <PilotCharts pilot={pilot} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* License Information */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center space-x-2">
              <MdWork className="text-primary-600 dark:text-primary-400" />
              <span>License</span>
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  License Number
                </label>
                <p className="text-neutral-900 dark:text-white">{pilot.licenseNumber || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Type
                </label>
                <p className="text-neutral-900 dark:text-white">{pilot.licenseType || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Expiry Date
                </label>
                <p className={`font-medium ${getExpiryColor(licenseExpiryStatus)}`}>
                  {formatDate(pilot.licenseExpiry)}
                  {licenseExpiryStatus === 'expired' && ' (Expired)'}
                  {licenseExpiryStatus === 'warning' &&
                    ` (${getDaysUntilExpiry(pilot.licenseExpiry)} days)`}
                </p>
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center space-x-2">
              <MdHealthAndSafety className="text-primary-600 dark:text-primary-400" />
              <span>Medical</span>
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Class
                </label>
                <p className="text-neutral-900 dark:text-white">{pilot.medicalClass || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Certificate Number
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {pilot.medicalCertificateNumber || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Last Medical
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {formatDate(pilot.dateOfLastMedical)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Next Medical
                </label>
                <p className={`font-medium ${getExpiryColor(medicalExpiryStatus)}`}>
                  {formatDate(pilot.dateOfNextMedical)}
                  {medicalExpiryStatus === 'expired' && ' (Expired)'}
                  {medicalExpiryStatus === 'warning' &&
                    ` (${getDaysUntilExpiry(pilot.dateOfNextMedical)} days)`}
                </p>
              </div>
            </div>
          </div>

          {/* Training Information */}
          <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center space-x-2">
              <MdSchool className="text-primary-600 dark:text-primary-400" />
              <span>Training</span>
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Simulator Hours
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {pilot.simulatorHours?.toLocaleString() || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Last Simulator Check
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {formatDate(pilot.lastSimulatorCheck)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Next Simulator Check
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {formatDate(pilot.nextSimulatorCheck)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Recurrent Training Status
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {pilot.recurrentTrainingStatus || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Last Training
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {formatDate(pilot.dateOfLastTraining)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                  Next Training
                </label>
                <p className="text-neutral-900 dark:text-white">
                  {formatDate(pilot.dateOfNextTraining)}
                </p>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          {pilot.emergencyContact && (
            <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center space-x-2">
                <MdEmergency className="text-primary-600 dark:text-primary-400" />
                <span>Emergency Contact</span>
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    Name
                  </label>
                  <p className="text-neutral-900 dark:text-white">{pilot.emergencyContact}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                    Phone
                  </label>
                  <div className="text-center">
                    <p className="text-neutral-900 dark:text-white flex items-center justify-center space-x-1">
                      <MdPhone className="text-sm" />
                      <span>{pilot.emergencyContactPhone}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PilotDetailPage
